import { RouteLocationNormalized } from 'vue-router';

import { SocialLogin } from '@capgo/capacitor-social-login';

import { loginSocialMobileRefresh } from '@/api/apiService';
import { AuthSession, SocialLoginProvider } from '@/types/components';
import { IResponseLoginSocialMobileRefresh } from '@/types/response';
import { isInCapacitorApp } from '@/utils/device';
import { secureStorageSet, secureStorageGet, secureStorageRemove } from '@/utils/native/nativeBridge';
import redirectToLoginPage, { goToMobileLoginPage } from '@/utils/redirectToLoginPage';

export const checkAuth = (to: RouteLocationNormalized): boolean => {
  return to.matched.some((record) => record.meta.requiresAuth) && !localStorage.getItem('authToken');
};

export const checkLoginType = (to: RouteLocationNormalized): boolean => {
  const { meta: { domain = '' } = { domain: '' } } = to || {};
  return (
    ['DISTRIBUTOR', 'HEAD_OFFICE', 'SELF_SERVICE'].includes(domain) && localStorage.getItem('loginType') !== domain
  );
};

export const redirectToLogin = (to: RouteLocationNormalized) => {
  if (isInCapacitorApp()) {
    goToMobileLoginPage();
    return;
  }

  const { meta: { domain = '' } = { domain: '' } } = to || {};
  if (domain === 'SELF_SERVICE') {
    let destinationUrl = '/login/ss';
    const params = new URLSearchParams();
    const partnerCode = localStorage.getItem('partner-code-admin');

    if (partnerCode) params.set('partnerCode', partnerCode);
    params.set('redirectTo', to.fullPath);
    destinationUrl += `?${params.toString()}`;

    window.location.replace(destinationUrl);
  } else {
    redirectToLoginPage(to.fullPath);
  }
};

export const refreshedCredentialsNative = async (): Promise<IResponseLoginSocialMobileRefresh> => {
  try {
    const authSession: AuthSession = await getAuthSession();
    const resLogin = await loginSocialMobileRefresh({
      provider: authSession.provider,
      refreshToken: authSession.refreshToken,
    });

    const { data, success } = resLogin;

    if (!success) {
      await logoutSocialMobile();
      throw Error('리프레시 토큰으로 로그인 하는 API 가 실패했습니다');
    }

    const { refreshToken } = data;

    await secureStorageSet({
      key: 'authSession',
      value: JSON.stringify({ refreshToken, provider: authSession.provider }),
    });

    return data;
  } catch (error) {
    throw new Error('리프레시 토큰으로 로그인하면서 에러가 발생했습니다', { cause: error });
  }
};

export const getAuthSession = async (): Promise<AuthSession> => {
  try {
    const value = await secureStorageGet({ key: 'authSession' });

    const parsed = JSON.parse(value) as Partial<AuthSession>;

    if (!parsed?.provider || !parsed?.refreshToken) throw Error('provider/refreshToken 이 없습니다');
    return parsed as AuthSession;
  } catch (error) {
    throw Error('authSession 조회를 실패했습니다');
  }
};

export const logoutSocialMobile = async (oAuth?: SocialLoginProvider) => {
  try {
    if (!oAuth) oAuth = await getOAuth();
    if (oAuth) {
      try {
        await SocialLogin.logout({ provider: oAuth as SocialLoginProvider });
      } catch {
        console.error('social login logout 실패');
      }
      await secureStorageRemove({ key: 'authToken' });
    }
    await removeAuthSession();
  } catch (error) {
    console.error('logout social mobile', error);
    throw Error('모바일 로그아웃 실패');
  }
};

export const getOAuth = async () => {
  let oAuth: SocialLoginProvider | undefined;

  try {
    const authSession = await secureStorageGet({ key: 'authSession' });
    const { provider } = JSON.parse(authSession || '{}');
    oAuth = provider?.split('_').at(-1)?.toLowerCase();
    if (oAuth === 'meta') oAuth = 'facebook';
  } catch (error) {
    console.error('oAuth 세션 조회 실패', error);
  }
  return oAuth;
};

export const removeAuthSession = async () => {
  try {
    await secureStorageRemove({ key: 'authSession' });
  } catch (error) {
    console.error('Failed to remove auth session', error);
  }
};
